import { ScriptEditor } from 'peak-editor-components';
import { workflowScriptEnvironment } from '../../../utils/workflowScriptEnvironment';

interface VariableDefinition {
  name: string;
  type: string;
  sourceNodeId: string;
  sourceNodeLabel: string;
  description?: string;
  example?: any;
}

interface ScriptTabProps {
  data: any;
  onChange: (key: string, value: any) => void;
  context?: VariableDefinition[];
}

/**
 * Build a flat variables object from the extracted variable context.
 * All workflow variables are top-level. System variables are grouped under `sys`.
 */
const buildVariablesFromContext = (context: VariableDefinition[]): Record<string, any> => {
    const vars: Record<string, any> = {};
    const sys: Record<string, any> = {};

    for (const v of context) {
        if (v.sourceNodeId === 'system') {
            sys[v.name] = v.example ?? v.type ?? 'sample';
        } else {
            vars[v.name] = v.example ?? v.type ?? 'sample';
        }
    }

    if (Object.keys(sys).length > 0) vars.sys = sys;
    return vars;
};

export const ScriptTab: React.FC<ScriptTabProps> = ({ data, onChange, context = [] }) => {
  const scriptContent = data.script || '// Variables are available directly by name\n// System info is in the `sys` object\nreturn {\n  result: myVar + 1\n};';

  const handleChange = (val: string) => onChange('script', val);

  const variables = buildVariablesFromContext(context);

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="shrink-0 p-4 bg-white border-b border-slate-200">
        <p className="text-xs text-slate-500">
          Write JavaScript (Node.js) code to manipulate workflow variables.
          Variables are available directly by name. System context is in <code>sys</code>. Return an object to merge results back.
        </p>
      </div>

      <div className="flex-1 relative border-t border-slate-200 min-h-[400px]">
        <ScriptEditor
            content={scriptContent}
            onChange={handleChange}
            variables={variables}
            variablesJson={JSON.stringify(variables, null, 2)}
            onVariablesChange={() => {}}
            variableError={null}
            functions={[]}
            onFunctionsChange={() => {}}
            scriptEnvironment={workflowScriptEnvironment}
        />
      </div>
    </div>
  );
};
